import React, { useState, useEffect, useRef} from "react";
import Layout from "@/components/Layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { api, getApiErrorMessage } from "@/lib/api";
import {
  TANK_SENSOR_TYPE_OPTIONS,
  getTankSensorTypeCode,
  getTankSensorTypeLabel,
  normalizeTankSensorType,
} from "@/lib/tankSensorTypes";
import {
  GSM_APN_OPTIONS,
  LEVEL_CONNECTION_TYPE_OPTIONS,
  getGsmApnCode,
  getGsmApnLabel,
  getLevelConnectionTypeCode,
  getLevelConnectionTypeLabel,
} from "@/lib/networkConnectionTypes";
import {
  Building2,
  Droplets,
  Zap,
  Network,
  Activity,
  Waves,
  Edit2,
  Trash2,
  X,
  Loader,
  RefreshCw,
  Search,
} from "lucide-react";
import mqtt from "mqtt";

const MQTT_BROKER_URL =
  process.env.REACT_APP_MQTT_BROKER_URL || "wss://aquatank.aquasense.tech:9001";
const MQTT_USERNAME = process.env.REACT_APP_MQTT_USERNAME || "aqua-tank";
const MQTT_PASSWORD = process.env.REACT_APP_MQTT_PASSWORD || "fIExd783QL9z";
const MQTT_D2C_TOPIC = process.env.REACT_APP_MQTT_D2C_TOPIC || "AQUATANK/D2C/+";
const MQTT_C2D_PREFIX = process.env.REACT_APP_MQTT_C2D_PREFIX || "AQUATANK/C2D";

const filterData = (data, searchTerm, searchFields) => {
  if (!searchTerm) return data;
  return data.filter((item) =>
    searchFields.some((field) =>
      String(item[field] || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()),
    ),
  );
};

const getAssetId = (item, assetType) => {
  const idMap = {
    tower: "id",
    ug_tank: "id",
    atbc: "device_id",
    aquatank: "device_id",
    pump: "id",
    tank: "id",
    valve: "id",
  };
  return item[idMap[assetType]] || item.id;
};

const assetTypeMap = {
  towers: "tower",
  "ug-tanks": "ug_tank",
  "pump-controllers": "atbc",
  "level-controllers": "aquatank",
  pumps: "pump",
  tanks: "tank",
  valves: "valve",
};

const DeleteButton = ({ assetType, id, onDelete }) => (
  <Button
    variant="ghost"
    size="sm"
    className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
    onClick={() => onDelete(assetType, id)}
    title={`Delete this ${assetType}`}
  >
    <Trash2 className="h-4 w-4" />
  </Button>
);

const EditButton = ({ assetType, item, onEdit }) => (
  <Button
    variant="ghost"
    size="sm"
    className="h-8 w-8 p-0 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
    onClick={() => onEdit(assetType, item)}
    title={`Edit this ${assetType}`}
  >
    <Edit2 className="h-4 w-4" />
  </Button>
);

const AssetTable = ({
  data,
  columns,
  emptyMessage,
  assetType,
  hasActions = false,
  onEdit,
  onDelete,
}) => (
  <div className="rounded-lg border border-slate-200 overflow-hidden">
    <Table>
      <TableHeader className="bg-slate-50">
        <TableRow>
          {columns.map((col) => (
            <TableHead key={col.key} className="font-semibold text-slate-700">
              {col.label}
            </TableHead>
          ))}
          {hasActions && (
            <TableHead className="w-24 text-center font-semibold text-slate-700">
              Actions
            </TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.length > 0 ? (
          data.map((item, idx) => (
            <TableRow
              key={item.id || idx}
              className="hover:bg-slate-50 transition-colors"
            >
              {columns.map((col) => (
                <TableCell key={col.key} className="py-3">
                  <span className="text-slate-700">
                    {col.render
                      ? col.render(item[col.key], item)
                      : item[col.key] || "-"}
                  </span>
                </TableCell>
              ))}
              {hasActions && (
                <TableCell className="py-3 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <EditButton
                      assetType={assetType}
                      item={item}
                      onEdit={onEdit}
                    />
                    <DeleteButton
                      assetType={assetType}
                      id={getAssetId(item, assetType)}
                      onDelete={onDelete}
                    />
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell
              colSpan={columns.length}
              className="text-center py-8 text-slate-500"
            >
              {emptyMessage}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </div>
);

const AssetSection = ({
  title,
  icon: Icon,
  data,
  searchTerm,
  setSearchTerm,
  columns,
  bgColor,
  emptyMessage,
  onEdit,
  onDelete,
}) => {
  const assetType =
    assetTypeMap[title.toLowerCase().replace(" ", "-")] || "unknown";

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className={`p-2.5 rounded-lg text-white ${bgColor}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900">{title}</h2>
          <p className="text-sm text-slate-500">
            Total: <span className="font-semibold">{data.length}</span>
          </p>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder={`Search ${title.toLowerCase()}...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      <AssetTable
        data={filterData(
          data,
          searchTerm,
          columns.map((c) => c.key),
        )}
        columns={columns}
        emptyMessage={emptyMessage}
        assetType={assetType}
        hasActions={true}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </div>
  );
};

const Components = () => {

  const clientRef = useRef(null);
  // States for all assets
  const [ugTanks, setUgTanks] = useState([]);
  const [towers, setTowers] = useState([]);
  const [pumpControllers, setPumpControllers] = useState([]);
  const [levelControllers, setLevelControllers] = useState([]);
  const [pumps, setPumps] = useState([]);
  const [tanks, setTanks] = useState([]);
  const [valves, setValves] = useState([]);
  const [flows, setFlows] = useState([]);

  // Search states
  const [searchUg, setSearchUg] = useState("");
  const [searchTowers, setSearchTowers] = useState("");
  const [searchPumpCtrl, setSearchPumpCtrl] = useState("");
  const [searchLevelCtrl, setSearchLevelCtrl] = useState("");
  const [searchPumps, setSearchPumps] = useState("");
  const [searchTanks, setSearchTanks] = useState("");
  const [searchValves, setSearchValves] = useState("");

  // Loading states
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const normalizeOptionalTankSensorType = (value) =>
    value === null || value === undefined || String(value).trim() === ""
      ? ""
      : normalizeTankSensorType(value);

  const normalizeOptionalConnectionType = (value) =>
    value === null || value === undefined || String(value).trim() === ""
      ? ""
      : getLevelConnectionTypeCode(value);

  const normalizeOptionalGsmApn = (value) =>
    value === null || value === undefined || String(value).trim() === ""
      ? ""
      : getGsmApnCode(value);

  const editConfigs = {
    tower: {
      label: "Tower",
      fields: [
        { key: "name", label: "Name" },
        { key: "location", label: "Location" },
        { key: "active", label: "Active", type: "checkbox" },
      ],
      update: api.updateTower,
    },
    ug_tank: {
      label: "UG Tank",
      fields: [
        { key: "ug_tank_id", label: "Tank ID" },
        { key: "name", label: "Name" },
        { key: "capacity", label: "Capacity", type: "number" },
        { key: "height", label: "Height", type: "number" },
        {
          key: "sensor_type",
          label: "Sensor Type",
          type: "select",
          options: TANK_SENSOR_TYPE_OPTIONS,
          normalize: normalizeOptionalTankSensorType,
        },
        {
          key: "sensor_slave_id",
          label: "Sensor Slave ID",
          type: "number",
          min: 1,
          max: 500,
        },
      ],
      update: api.updateUGTank,
    },
    atbc: {
      label: "Pump Controller",
      fields: [
        { key: "device_id", label: "Device ID" },
        { key: "name", label: "Name" },
        {
          key: "connectionType",
          path: "network.connectionType",
          label: "Connection Type",
          type: "select",
          valueType: "number",
          options: LEVEL_CONNECTION_TYPE_OPTIONS,
          normalize: normalizeOptionalConnectionType,
        },
        {
          key: "gsmISP",
          path: "network.gsmISP",
          label: "GSM ISP",
          type: "select",
          valueType: "number",
          options: GSM_APN_OPTIONS,
          normalize: normalizeOptionalGsmApn,
        },
        {
          key: "wifiUsn",
          path: "network.wifiUsn",
          label: "WiFi Username",
        },
        {
          key: "wifiPwd",
          path: "network.wifiPwd",
          label: "WiFi Password",
        },
      ],
      update: api.updateATBC,
    },
    aquatank: {
      label: "Level Controller",
      fields: [
        { key: "device_id", label: "Device ID" },
        { key: "name", label: "Name" },
        {
          key: "connectionType",
          path: "network.connectionType",
          label: "Connection Type",
          type: "select",
          valueType: "number",
          options: LEVEL_CONNECTION_TYPE_OPTIONS,
          normalize: normalizeOptionalConnectionType,
        },
        {
          key: "gsmISP",
          path: "network.gsmISP",
          label: "GSM ISP",
          type: "number",
        },
        {
          key: "wifiUsn",
          path: "network.wifiUsn",
          label: "WiFi Username",
        },
        {
          key: "wifiPwd",
          path: "network.wifiPwd",
          label: "WiFi Password",
        },
      ],
      update: api.updateAquaTank,
    },
    pump: {
      label: "Pump",
      fields: [
        { key: "pump_id", label: "Pump ID" },
        { key: "name", label: "Name" },
        { key: "power_rating", label: "Power Rating", type: "number" },
        { key: "model", label: "Model" },
        {
          key: "energyMeterStatus",
          path: "energymeter.status",
          label: "Energy Meter Status",
          type: "select",
          valueType: "number",
          options: [
            { value: "1", label: "Connected" },
            { value: "0", label: "Not connected" },
          ],
        },
        {
          key: "energyMeterMake",
          path: "energymeter.make",
          label: "Energy Meter Make",
          type: "number",
        },
        {
          key: "energyMeterSlaveId",
          path: "energymeter.slave_id",
          label: "Energy Meter Slave ID",
          type: "number",
          min: 1,
          max: 500,
        },
        { key: "control", label: "Control", type: "checkbox" },
      ],
      update: api.updatePump,
    },
    tank: {
      label: "Tank",
      fields: [
        { key: "tank_id", label: "Tank ID" },
        { key: "name", label: "Name" },
        { key: "capacity", label: "Capacity", type: "number" },
        {
          key: "sensor_type",
          label: "Sensor Type",
          type: "select",
          options: TANK_SENSOR_TYPE_OPTIONS,
          normalize: normalizeOptionalTankSensorType,
        },
        {
          key: "sensor_slave_id",
          label: "Sensor Slave ID",
          type: "number",
          min: 1,
          max: 500,
        },
        { key: "low", label: "Low", type: "number", min: 0, max: 100 },
        { key: "med", label: "Med", type: "number", min: 0, max: 100 },
        { key: "high", label: "High", type: "number", min: 0, max: 100 },
        { key: "height", label: "Height", type: "number" },
        { key: "control", label: "Control", type: "checkbox" },
      ],
      update: api.updateTank,
    },
    valve: {
      label: "Valve",
      fields: [
        { key: "valve_id", label: "Valve ID" },
        {
          key: "type",
          label: "Type",
          type: "select",
          options: [
            { value: "motorized", label: "Motorized" },
            { value: "manual", label: "Manual" },
          ],
        },
        { key: "control", label: "Control", type: "checkbox" },
      ],
      update: api.updateValve,
    },
  };

  // Fetch all assets
  const fetchAllAssets = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const [
        ugTanksRes,
        towersRes,
        pumpControllersRes,
        levelControllersRes,
        pumpsRes,
        tanksRes,
        valvesRes,
        flowsRes,
      ] = await Promise.all([
        api.getUGTanks().catch(() => ({ data: [] })),
        api.getTowers().catch(() => ({ data: [] })),
        api.getPumpControllers().catch(() => ({ data: [] })),
        api.getLevelControllers().catch(() => ({ data: [] })),
        api.getPumps().catch(() => ({ data: [] })),
        api.getTanks().catch(() => ({ data: [] })),
        api.getValves().catch(() => ({ data: [] })),
        api.getFlows().catch(() => ({ data: [] })),
      ]);

      setUgTanks(ugTanksRes.data || []);
      setTowers(towersRes.data || []);
      setPumpControllers(pumpControllersRes.data || []);
      setLevelControllers(levelControllersRes.data || []);
      setPumps(pumpsRes.data || []);
      setTanks(tanksRes.data || []);
      setValves(valvesRes.data || []);
      setFlows(flowsRes || []);

      if (isRefresh) {
        toast.success("Assets refreshed successfully");
      }
    } catch (error) {
      toast.error("Failed to load assets");
      console.error(error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

const getNumberOrDefault = (value, defaultValue) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : defaultValue;
};

const getLevelControllerNetwork = (deviceId) => {
  const controller = levelControllers.find((item) => item.device_id === deviceId);
  const network = controller?.network || {};

  return {
    connectionType: getLevelConnectionTypeCode(network.connectionType),
    gsmISP: getNumberOrDefault(network.gsmISP, 1),
    wifiUsn: network.wifiUsn || "",
    wifiPwd: network.wifiPwd || "",
  };
};

const getPumpControllerNetwork = (deviceId) => {
  const controller = pumpControllers.find((item) => item.device_id === deviceId);
  const network = controller?.network || {};

  return {
    connection_type: getLevelConnectionTypeCode(network.connectionType),
    gsm_isp: getGsmApnCode(network.gsmISP),
    wifi_username: network.wifiUsn || "",
    wifi_password: network.wifiPwd || "",
  };
};

const sendPumpConfig = (deviceId) => {
  let config = {};

  // ✅ Step 1: find pumps for this controller
  const matchedFlows = flows.filter(
    (item) => item.pump_controller_id === deviceId,
  );

  if (matchedFlows.length === 0) {
    toast.error("This pump controller is not added to any flows");
    return;
  }

  // ✅ Step 2: build UG tank → tanks mapping
  const pumpMap = new Map();

  matchedFlows.forEach((flow) => {
    (flow.valve_tank_mappings || []).forEach((mapping) => {
      const pumpId = mapping.pump_id;
      if (!pumpId) return;

      if (!pumpMap.has(pumpId)) {
        pumpMap.set(pumpId, {
          pump_id: pumpId,
          associated_tanks: [],
          associated_ugts: [],
        });
      }

      const pumpConfig = pumpMap.get(pumpId);
      if (mapping.tank_id && !pumpConfig.associated_tanks.includes(mapping.tank_id)) {
        pumpConfig.associated_tanks.push(mapping.tank_id);
      }
      if (flow.ug_tank_id && !pumpConfig.associated_ugts.includes(flow.ug_tank_id)) {
        pumpConfig.associated_ugts.push(flow.ug_tank_id);
      }
    });
  });

  const pumpConfigs = Array.from(pumpMap.values());
  if (pumpConfigs.length === 0) {
    toast.error("No pumps are mapped to this pump controller");
    return;
  }

  // ✅ Step 3: build final config
  config.device_id = matchedFlows[0].pump_controller_id;
  config.society_id = matchedFlows[0].society_id;
  config.device_type = "p_controller";
  config.config_type = "d_config";
  config.network = getPumpControllerNetwork(deviceId);

  pumpConfigs.forEach((pump, index) => {
    const ugTankId = pump.associated_ugts[0] || "-";
    const ugTankMeta = ugTanks.find((tank) => tank.ug_tank_id === ugTankId);
    const pumpMeta = pumps.find((item) => item.pump_id === pump.pump_id);
    const energyMeter = pumpMeta?.energymeter || {};

    config[`pump${index + 1}`] = {
      pump_id: pump.pump_id,
      associated_ugt: ugTankId,
      ugt_tank_height: getNumberOrDefault(ugTankMeta?.height, 0),
      ugt_sensor_type: getTankSensorTypeCode(ugTankMeta?.sensor_type),
      ugt_sensor_slave_id: getNumberOrDefault(ugTankMeta?.sensor_slave_id, 1),
      tank_count: pump.associated_tanks.length,
      associated_tanks: pump.associated_tanks,
      energymeter: {
        status: getNumberOrDefault(energyMeter.status, 1),
        make: getNumberOrDefault(energyMeter.make, 2),
        slave_id: getNumberOrDefault(energyMeter.slave_id, 1),
      },
    };
  });
  // // console.log(config, "Sending config to pump controller");
  const topic = `${MQTT_C2D_PREFIX}/${deviceId}`;

  if (clientRef.current && clientRef.current.connected) {
    clientRef.current.publish(
      topic,
      JSON.stringify(config),
      { qos: 1 }, // ensure delivery
      (err) => {
        if (err) {
          console.error("❌ Publish failed:", err);
          toast.error("Failed to send config");
        } else {
          // // console.log("✅ Config published to", topic);
          toast.success("Config sent successfully");
        }
      },
    );
  } else {
    console.error("❌ MQTT not connected");
    toast.error("MQTT not connected");
  }
};

  const sendLevelConfig = (deviceId) => {
    let config = {
      device_type: "v_controller",
      config_type: "d_config",
    };

    // ✅ Step 1: filter flows for this level controller
    const matchedFlows = flows.filter(
      (item) => item.level_controller_id === deviceId,
    );

    if (matchedFlows.length === 0) {
      toast.error("This level controller is not added to any flows");
      return;
    }

    // ✅ Step 2: set common fields
    config.device_id = matchedFlows[0].level_controller_id;
    config.society_id = matchedFlows[0].society_id;
    config.tower_id = matchedFlows[0].tower_id;
    config.bmeoffset = 0.0;
    config.network = getLevelControllerNetwork(deviceId);

    // ✅ Step 3: build tank mappings
    let tankIndex = 1;

    matchedFlows.forEach((flow) => {
      if (flow.valve_tank_mappings?.length > 0) {
        flow.valve_tank_mappings.forEach((mapping) => {
          const tankMeta = tanks.find((tank) => tank.tank_id === mapping.tank_id);

          config[`tank${tankIndex}`] = {
            tank_id: mapping.tank_id || "-",
            sensor_type: getTankSensorTypeCode(tankMeta?.sensor_type),
            sensor_slave_id: getNumberOrDefault(tankMeta?.sensor_slave_id, 1),
            tank_height: getNumberOrDefault(tankMeta?.height, 0),
            associated_pump_id: mapping.pump_id || "-",
            associated_valve_id: mapping.valve_id || "-",
          };

          tankIndex++;
        });
      }
    });

    (config, "Sending config to level controller");

    // ✅ Step 4: Publish to MQTT
    const topic = `${MQTT_C2D_PREFIX}/${deviceId}`;

    if (clientRef.current && clientRef.current.connected) {
      clientRef.current.publish(
        topic,
        JSON.stringify(config),
        { qos: 1 },
        (err) => {
          if (err) {
            console.error("❌ Publish failed:", err);
            toast.error("Failed to send config");
          } else {
            // // console.log("✅ Level config published");
            toast.success("Level config sent successfully");
          }
        },
      );
    } else {
      toast.error("MQTT not connected");
    }
  };

  // const sendLevelConfig = (deviceId) => {
  //   // console.log("Sending level config to device:", deviceId);
  //   toast.success(`Config sent to level controller ${deviceId}`);
  // };

  const updatePumpConfiguration = async (device_id) => {
    try {
      const res = await api.patchATBCs(device_id);
      if (res.success) {
        toast.success(
          `Pump controller configured successfully`,
        );
       }
      return res;
    } catch (error) {
      toast.error(`${error}`);
      return { data: [] };
    }
  };

  
  const updateLevelConfiguration = async (device_id) => {
    try {
      const res = await api.patchAquaTanks(device_id);
      if (res.success) {
        toast.success(
          `Level controller configured successfully`,
        );
       }
      return res;
    } catch (error) {
      toast.error(`${error}`);
      return { data: [] };
    }
  };

  useEffect(() => {
    fetchAllAssets();
  }, []);

  // MQTT SETUP
  useEffect(() => {
    const client = mqtt.connect(MQTT_BROKER_URL, {
      username: MQTT_USERNAME,
      password: MQTT_PASSWORD,
      clientId: `at_comp_${Math.random().toString(16).slice(2, 10)}`,
      clean: true,
      connectTimeout: 10000,
      reconnectPeriod: 3000,
    });

    clientRef.current = client;

    client.on("connect", () => {
      // console.log("✅ MQTT Connected", MQTT_BROKER_URL);

      client.subscribe(MQTT_D2C_TOPIC, { qos: 1 }, (err) => {
        if (err) {
          console.error("❌ MQTT subscribe failed:", err);
          return;
        }
        // console.log("Subscribed to", MQTT_D2C_TOPIC);
      });
    });

    client.on("message", (topic, message) => {
      let payload;
      try {
        payload = JSON.parse(message.toString());
      } catch (err) {
        console.error("Invalid MQTT message", topic, err);
        return;
      }

      // console.log(payload, "Received message from device");
      const device_id = payload.device_id
      if (payload.set_config_result !== "pass") {
        toast.error("Device configuration has been failed");
      } else if (
        payload.device_type === "p_controller" &&
        payload.config_type === "d_config"
      ) {
        updatePumpConfiguration(device_id);
      } else if (
        payload.device_type === "v_controller" &&
        payload.config_type === "d_config"
      ) {
        updateLevelConfiguration(device_id);
      }
      else { 
        toast.error("Unknown message received");
      }
    });

    client.on("error", (err) => {
      console.error("❌ MQTT Error:", err);
    });

    client.on("reconnect", () => {
      // console.log("🔄 Reconnecting to MQTT broker...");
    });

    client.on("offline", () => {
      console.warn("MQTT client is offline");
    });

    client.on("close", () => {
      console.warn("MQTT connection closed");
    });

    client.on("end", () => {
      // console.log("MQTT client ended");
    });

    return () => {
      if (clientRef.current === client) {
        clientRef.current = null;
      }
      client.end(true);
      // console.log("🔌 MQTT client stopped");
    };
  }, []);

  // Delete handler
  const handleDelete = async (assetType, id) => {
    const typeLabels = {
      tower: "Tower",
      ug_tank: "UG Tank",
      atbc: "Pump Controller",
      aquatank: "Level Controller",
      pump: "Pump",
      tank: "Tank",
      valve: "Valve",
    };

    if (
      !window.confirm(
        `Delete this ${typeLabels[assetType] || assetType} (ID: ${id})?`,
      )
    ) {
      return;
    }

    try {
      let result;
      switch (assetType) {
        case "tower":
          result = await api.deleteTower(id);
          break;
        case "ug_tank":
          result = await api.deleteUGTank(id);
          break;
        case "atbc":
          result = await api.deleteATBC(id);
          break;
        case "aquatank":
          result = await api.deleteAquaTank(id);
          break;
        case "pump":
          result = await api.deletePump(id);
          break;
        case "tank":
          result = await api.deleteTank(id);
          break;
        case "valve":
          result = await api.deleteValve(id);
          break;
        default:
          throw new Error("Unknown asset type");
      }

      toast.success(
        `${typeLabels[assetType] || assetType} deleted successfully`,
      );
      await fetchAllAssets(true); // Refresh
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error(
        getApiErrorMessage(
          error,
          `Failed to delete ${typeLabels[assetType] || assetType}`,
        ),
      );
    }
  };

  const getNestedValue = (source, path) =>
    path.split(".").reduce((value, key) => value?.[key], source);

  const setNestedValue = (target, path, value) => {
    const keys = path.split(".");
    let cursor = target;

    keys.slice(0, -1).forEach((key) => {
      cursor[key] = cursor[key] || {};
      cursor = cursor[key];
    });

    cursor[keys[keys.length - 1]] = value;
  };

  const openEdit = (assetType, item) => {
    const config = editConfigs[assetType];
    const form = {};
    config.fields.forEach((field) => {
      const value = field.path ? getNestedValue(item, field.path) : item[field.key];
      const normalizedValue = field.normalize ? field.normalize(value) : value;
      form[field.key] =
        normalizedValue ?? (field.type === "checkbox" ? false : "");
    });
    setEditingAsset({ assetType, item });
    setEditForm(form);
  };

  const closeEdit = () => {
    setEditingAsset(null);
    setEditForm({});
  };

  const handleEditSubmit = async () => {
    if (!editingAsset) return;

    const { assetType, item } = editingAsset;
    const config = editConfigs[assetType];
    const payload = {};

    config.fields.forEach((field) => {
      const value = editForm[field.key];
      if (field.type === "number" || field.valueType === "number") {
        const nextValue = value === "" || value === null ? null : Number(value);
        if (field.path) {
          setNestedValue(payload, field.path, nextValue);
        } else {
          payload[field.key] = nextValue;
        }
      } else {
        if (field.path) {
          setNestedValue(payload, field.path, value);
        } else {
          payload[field.key] = value;
        }
      }
    });

    setSavingEdit(true);
    try {
      await config.update(getAssetId(item, assetType), payload);
      toast.success(`${config.label} updated successfully`);
      closeEdit();
      await fetchAllAssets(true);
    } catch (error) {
      console.error("Update failed:", error);
      toast.error(error.response?.data?.detail || `Failed to update ${config.label}`);
    } finally {
      setSavingEdit(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-screen">
          <Loader className="h-12 w-12 text-blue-500 animate-spin" />
          <p className="mt-4 text-slate-600">Loading assets...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900">Assets</h1>
            <p className="text-slate-600 mt-1">
              View and manage all system assets and devices
            </p>
          </div>
          <Button
            onClick={() => fetchAllAssets(true)}
            disabled={refreshing}
            className="flex items-center gap-2 bg-slate-900 hover:bg-slate-700"
          >
            <RefreshCw
              className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        </div>

        {editingAsset && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    Edit {editConfigs[editingAsset.assetType].label}
                  </h2>
                  <p className="text-xs text-slate-500">
                    Changes are applied to existing architecture references when IDs change.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeEdit}
                  className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="max-h-[65vh] space-y-4 overflow-y-auto px-5 py-4">
                {editConfigs[editingAsset.assetType].fields.map((field) => (
                  <div key={field.key}>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      {field.label}
                    </label>
                    {field.type === "checkbox" ? (
                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={Boolean(editForm[field.key])}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              [field.key]: e.target.checked,
                            }))
                          }
                          className="h-4 w-4"
                        />
                        Enabled
                      </label>
                    ) : field.type === "select" ? (
                      <select
                        value={editForm[field.key] ?? ""}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            [field.key]: e.target.value,
                          }))
                        }
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-slate-900 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      >
                        <option value="" disabled>
                          Select {field.label}
                        </option>
                        {field.options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        type={field.type || "text"}
                        min={field.min}
                        max={field.max}
                        value={editForm[field.key] ?? ""}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            [field.key]: e.target.value,
                          }))
                        }
                      />
                    )}
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
                <Button variant="outline" onClick={closeEdit} disabled={savingEdit}>
                  Cancel
                </Button>
                <Button
                  onClick={handleEditSubmit}
                  disabled={savingEdit}
                  className="bg-slate-900 hover:bg-slate-700"
                >
                  {savingEdit ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="towers" className="w-full">
          <TabsList className="grid w-full grid-cols-7 bg-slate-100 p-1 h-auto">
            <TabsTrigger
              value="towers"
              className="flex items-center gap-2 data-[state=active]:bg-white"
            >
              <Building2 className="h-4 w-4" />
              <span className="hidden sm:inline">Towers</span>
              <span className="sm:hidden text-xs">{towers.length}</span>
            </TabsTrigger>
            <TabsTrigger
              value="ug-tanks"
              className="flex items-center gap-2 data-[state=active]:bg-white"
            >
              <Droplets className="h-4 w-4" />
              <span className="hidden sm:inline">UG Tanks</span>
              <span className="sm:hidden text-xs">{ugTanks.length}</span>
            </TabsTrigger>
            <TabsTrigger
              value="pump-controllers"
              className="flex items-center gap-2 data-[state=active]:bg-white"
            >
              <Zap className="h-4 w-4" />
              <span className="hidden sm:inline">Pump Ctrl</span>
              <span className="sm:hidden text-xs">
                {pumpControllers.length}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="level-controllers"
              className="flex items-center gap-2 data-[state=active]:bg-white"
            >
              <Network className="h-4 w-4" />
              <span className="hidden sm:inline">Level Ctrl</span>
              <span className="sm:hidden text-xs">
                {levelControllers.length}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="pumps"
              className="flex items-center gap-2 data-[state=active]:bg-white"
            >
              <Activity className="h-4 w-4" />
              <span className="hidden sm:inline">Pumps</span>
              <span className="sm:hidden text-xs">{pumps.length}</span>
            </TabsTrigger>
            <TabsTrigger
              value="tanks"
              className="flex items-center gap-2 data-[state=active]:bg-white"
            >
              <Waves className="h-4 w-4" />
              <span className="hidden sm:inline">Tanks</span>
              <span className="sm:hidden text-xs">{tanks.length}</span>
            </TabsTrigger>
            <TabsTrigger
              value="valves"
              className="flex items-center gap-2 data-[state=active]:bg-white"
            >
              <Network className="h-4 w-4" />
              <span className="hidden sm:inline">Valves</span>
              <span className="sm:hidden text-xs">{valves.length}</span>
            </TabsTrigger>
          </TabsList>

          {/* Towers Tab */}
          <TabsContent value="towers" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Towers</CardTitle>
                <CardDescription>
                  All towers registered under the current super-admin society
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AssetSection
                  title="Towers"
                  icon={Building2}
                  data={towers}
                  searchTerm={searchTowers}
                  setSearchTerm={setSearchTowers}
                  columns={[
                    { key: "name", label: "Name" },
                    { key: "location", label: "Location" },
                    {
                      key: "active",
                      label: "Status",
                      render: (val) =>
                        val ? (
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-slate-100 text-slate-800 rounded-full text-xs font-medium">
                            Inactive
                          </span>
                        ),
                    },
                    {
                      key: "created_at",
                      label: "Created",
                      render: (val) =>
                        val ? new Date(val).toLocaleDateString() : "-",
                    },
                  ]}
                  bgColor="bg-gradient-to-br from-blue-500 to-blue-600"
                  emptyMessage="No towers found"
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* UG Tanks Tab */}
          <TabsContent value="ug-tanks" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Underground Tanks</CardTitle>
                <CardDescription>
                  All registered underground water tanks in the system
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AssetSection
                  title="UG Tanks"
                  icon={Droplets}
                  data={ugTanks}
                  searchTerm={searchUg}
                  setSearchTerm={setSearchUg}
                  columns={[
                    { key: "ug_tank_id", label: "Tank ID" },
                    { key: "name", label: "Name" },
                    {
                      key: "capacity",
                      label: "Capacity",
                      render: (val) => (val ? `${val} L` : "-"),
                    },
                    {
                      key: "height",
                      label: "Height",
                      render: (val) => (val ? `${val} m` : "-"),
                    },
                    {
                      key: "sensor_type",
                      label: "Sensor Type",
                      render: getTankSensorTypeLabel,
                    },
                    { key: "sensor_slave_id", label: "Sensor Slave ID" },
                    {
                      key: "created_at",
                      label: "Created",
                      render: (val) =>
                        val ? new Date(val).toLocaleDateString() : "-",
                    },
                  ]}
                  bgColor="bg-gradient-to-br from-cyan-500 to-cyan-600"
                  emptyMessage="No underground tanks found"
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pump Controllers Tab */}
          <TabsContent value="pump-controllers" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Pump Controllers</CardTitle>
                <CardDescription>
                  All pump control devices in the system
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AssetSection
                  title="Pump Controllers"
                  icon={Zap}
                  data={pumpControllers}
                  searchTerm={searchPumpCtrl}
                  setSearchTerm={setSearchPumpCtrl}
                  columns={[
                    { key: "device_id", label: "Device ID" },
                    { key: "name", label: "Name" },
                    {
                      key: "connectionType",
                      label: "Connection",
                      render: (val, item) =>
                        getLevelConnectionTypeLabel(item.network?.connectionType),
                    },
                    {
                      key: "gsmISP",
                      label: "GSM ISP",
                      render: (val, item) => getGsmApnLabel(item.network?.gsmISP),
                    },
                    {
                      key: "wifiUsn",
                      label: "WiFi User",
                      render: (val, item) => item.network?.wifiUsn || "-",
                    },
                    {
                      key: "device_conf",
                      label: "Configure Device",
                      render: (val, item) => (
                        <Button
                          onClick={() => sendPumpConfig(item.device_id)}
                          variant="outline"
                          size="sm"
                          className="h-8 px-3 text-xs"
                        >
                          {item.isConfigured ? "Reconfigure" : "Configure"}
                        </Button>
                      ),
                    },
                    {
                      key: "created_at",
                      label: "Created",
                      render: (val) =>
                        val ? new Date(val).toLocaleDateString() : "-",
                    },
                  ]}
                  bgColor="bg-gradient-to-br from-yellow-500 to-yellow-600"
                  emptyMessage="No pump controllers found"
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Level Controllers Tab */}
          <TabsContent value="level-controllers" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Level Controllers</CardTitle>
                <CardDescription>
                  All water level monitoring devices in the system
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AssetSection
                  title="Level Controllers"
                  icon={Network}
                  data={levelControllers}
                  searchTerm={searchLevelCtrl}
                  setSearchTerm={setSearchLevelCtrl}
                  columns={[
                    { key: "device_id", label: "Device ID" },
                    { key: "name", label: "Name" },
                    {
                      key: "connectionType",
                      label: "Connection",
                      render: (val, item) =>
                        getLevelConnectionTypeLabel(item.network?.connectionType),
                    },
                    {
                      key: "device_conf",
                      label: "Device Conf",
                      render: (val, item) => (
                        <Button
                          onClick={() => sendLevelConfig(item.device_id)}
                          variant="outline"
                          size="sm"
                          className="h-8 px-3 text-xs"
                        >
                          {item.isConfigured ? "Reconfigure" : "Configure"}
                        </Button>
                      ),
                    },
                    {
                      key: "created_at",
                      label: "Created",
                      render: (val) =>
                        val ? new Date(val).toLocaleDateString() : "-",
                    },
                  ]}
                  bgColor="bg-gradient-to-br from-indigo-500 to-indigo-600"
                  emptyMessage="No level controllers found"
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pumps Tab */}
          <TabsContent value="pumps" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Pumps</CardTitle>
                <CardDescription>
                  All water pumps registered in the system
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AssetSection
                  title="Pumps"
                  icon={Activity}
                  data={pumps}
                  searchTerm={searchPumps}
                  setSearchTerm={setSearchPumps}
                  columns={[
                    { key: "pump_id", label: "Pump ID" },
                    { key: "name", label: "Name" },
                    {
                      key: "power_rating",
                      label: "Power Rating",
                      render: (val) => (val ? `${val} W` : "-"),
                    },
                    { key: "model", label: "Model" },
                    {
                      key: "energyMeterStatus",
                      label: "EM Status",
                      render: (val, item) => item.energymeter?.status ?? "-",
                    },
                    {
                      key: "energyMeterMake",
                      label: "EM Make",
                      render: (val, item) => item.energymeter?.make ?? "-",
                    },
                    {
                      key: "energyMeterSlaveId",
                      label: "EM Slave",
                      render: (val, item) => item.energymeter?.slave_id ?? "-",
                    },
                    {
                      key: "control",
                      label: "Control",
                      render: (val) =>
                        val ? (
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                            Yes
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-slate-100 text-slate-800 rounded-full text-xs font-medium">
                            No
                          </span>
                        ),
                    },
                    {
                      key: "created_at",
                      label: "Created",
                      render: (val) =>
                        val ? new Date(val).toLocaleDateString() : "-",
                    },
                  ]}
                  bgColor="bg-gradient-to-br from-orange-500 to-orange-600"
                  emptyMessage="No pumps found"
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tanks Tab */}
          <TabsContent value="tanks" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Overhead Tanks</CardTitle>
                <CardDescription>
                  All overhead water storage tanks in the system
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AssetSection
                  title="Tanks"
                  icon={Waves}
                  data={tanks}
                  searchTerm={searchTanks}
                  setSearchTerm={setSearchTanks}
                  columns={[
                    { key: "tank_id", label: "Tank ID" },
                    { key: "name", label: "Name" },
                    {
                      key: "capacity",
                      label: "Capacity",
                      render: (val) => (val ? `${val} L` : "-"),
                    },
                    {
                      key: "sensor_type",
                      label: "Sensor Type",
                      render: getTankSensorTypeLabel,
                    },
                    { key: "sensor_slave_id", label: "Sensor Slave ID" },
                    { key: "low", label: "Low" },
                    { key: "med", label: "Med" },
                    { key: "high", label: "High" },
                    {
                      key: "height",
                      label: "Height",
                      render: (val) => (val ? `${val} m` : "-"),
                    },
                    {
                      key: "control",
                      label: "Control",
                      render: (val) =>
                        val ? (
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                            Yes
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-slate-100 text-slate-800 rounded-full text-xs font-medium">
                            No
                          </span>
                        ),
                    },
                    {
                      key: "created_at",
                      label: "Created",
                      render: (val) =>
                        val ? new Date(val).toLocaleDateString() : "-",
                    },
                  ]}
                  bgColor="bg-gradient-to-br from-teal-500 to-teal-600"
                  emptyMessage="No tanks found"
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Valves Tab */}
          <TabsContent value="valves" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Valves</CardTitle>
                <CardDescription>
                  All water control valves in the system
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AssetSection
                  title="Valves"
                  icon={Network}
                  data={valves}
                  searchTerm={searchValves}
                  setSearchTerm={setSearchValves}
                  columns={[
                    { key: "valve_id", label: "Valve ID" },
                    {
                      key: "type",
                      label: "Type",
                      render: (val) => (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                          {val || "Unknown"}
                        </span>
                      ),
                    },
                    {
                      key: "control",
                      label: "Control",
                      render: (val) =>
                        val ? (
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                            Yes
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-slate-100 text-slate-800 rounded-full text-xs font-medium">
                            No
                          </span>
                        ),
                    },
                    {
                      key: "created_at",
                      label: "Created",
                      render: (val) =>
                        val ? new Date(val).toLocaleDateString() : "-",
                    },
                  ]}
                  bgColor="bg-gradient-to-br from-pink-500 to-pink-600"
                  emptyMessage="No valves found"
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-700">
                  {towers.length}
                </p>
                <p className="text-xs text-blue-600 mt-1">Towers</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-cyan-700">
                  {ugTanks.length}
                </p>
                <p className="text-xs text-cyan-600 mt-1">UG Tanks</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-yellow-700">
                  {pumpControllers.length}
                </p>
                <p className="text-xs text-yellow-600 mt-1">Pump Ctrl</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-indigo-700">
                  {levelControllers.length}
                </p>
                <p className="text-xs text-indigo-600 mt-1">Level Ctrl</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-orange-700">
                  {pumps.length}
                </p>
                <p className="text-xs text-orange-600 mt-1">Pumps</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-teal-700">
                  {tanks.length}
                </p>
                <p className="text-xs text-teal-600 mt-1">Tanks</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-pink-50 to-pink-100 border-pink-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-pink-700">
                  {valves.length}
                </p>
                <p className="text-xs text-pink-600 mt-1">Valves</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Components;
